import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Boxes,
  Cable,
  ChevronRight,
  FolderGit2,
  LogOut,
  Menu,
  Sparkles,
  Settings,
  Workflow,
  X,
} from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';

import { apiRequest } from '../api/client.js';
import { useOptionalWebAuth } from '../features/auth/AuthGate.js';
import { useUiPreferences } from '../i18n/ui-preferences.js';
import { PreferenceControls } from './PreferenceControls.js';

const NAVIGATION = [
  { to: '/', zh: '工作项', en: 'Work Items', icon: Boxes, end: true },
  { to: '/workflows', zh: '工作流', en: 'Workflows', icon: Workflow },
  { to: '/skills', zh: 'Skills', en: 'Skills', icon: Sparkles },
  { to: '/projects', zh: '项目', en: 'Projects', icon: FolderGit2 },
  { to: '/integrations', zh: '集成', en: 'Integrations', icon: Cable },
  { to: '/settings', zh: '设置', en: 'Settings', icon: Settings },
] as const;

export function AppShell() {
  const { t } = useUiPreferences();
  const auth = useOptionalWebAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    document.body.classList.add('navigation-open');
    closeButtonRef.current?.focus();
    const menuButton = menuButtonRef.current;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.classList.remove('navigation-open');
      window.removeEventListener('keydown', handleKeyDown);
      menuButton?.focus();
    };
  }, [menuOpen]);
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => apiRequest<{ status: string }>('/health/live'),
    refetchInterval: 15_000,
    retry: 1,
  });
  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        {t('跳到主要内容', 'Skip to main content')}
      </a>
      <aside id="primary-navigation" className={`primary-sidebar${menuOpen ? ' is-open' : ''}`}>
        <div className="brand-row">
          <NavLink
            to="/"
            className="brand"
            onClick={() => setMenuOpen(false)}
            aria-label={t('TCode 首页', 'TCode home')}
          >
            <span className="brand-mark">T</span>
            <span>
              <strong>TCode</strong>
              <small>Delivery OS</small>
            </span>
          </NavLink>
          <button
            ref={closeButtonRef}
            className="mobile-close"
            type="button"
            aria-label={t('关闭导航', 'Close navigation')}
            onClick={() => setMenuOpen(false)}
          >
            <X />
          </button>
        </div>
        <nav aria-label={t('主导航', 'Main navigation')}>
          {NAVIGATION.map(({ to, zh, en, icon: Icon, ...item }) => (
            <NavLink
              key={to}
              to={to}
              end={'end' in item ? item.end : false}
              onClick={() => setMenuOpen(false)}
            >
              <Icon aria-hidden="true" />
              <span>{t(zh, en)}</span>
              <ChevronRight className="nav-chevron" aria-hidden="true" />
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-foot">
          <span className={`health-indicator${health.isSuccess ? ' is-healthy' : ''}`}>
            <span aria-hidden="true" />
            {health.isSuccess
              ? t('控制平面在线', 'Control plane online')
              : t('正在检查控制平面', 'Checking control plane')}
          </span>
          <small>{t('自动交付止于测试部署', 'Automation stops at test deployment')}</small>
        </div>
      </aside>
      <div className="app-column">
        <header className="topbar">
          <button
            ref={menuButtonRef}
            className="mobile-menu"
            type="button"
            aria-label={t('打开导航', 'Open navigation')}
            aria-controls="primary-navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Menu />
          </button>
          <div>
            <span className="topbar-context">
              {t(
                '独立沙箱 · 可恢复工作流 · 人工合并',
                'Isolated sandboxes · Resumable workflows · Human merge',
              )}
            </span>
          </div>
          <div className="topbar-actions">
            <PreferenceControls />
            {auth?.user ? (
              <div className="user-session">
                {auth.user.avatarUrl ? (
                  <img
                    src={auth.user.avatarUrl}
                    alt={auth.user.name}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="user-session-initial" aria-hidden="true">
                    {auth.user.name.slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="user-session-copy">
                  <strong>{auth.user.name}</strong>
                  <small>{t('飞书已验证', 'Verified by Feishu')}</small>
                </span>
                <button
                  className="user-session-logout"
                  type="button"
                  aria-label={t('退出登录', 'Sign out')}
                  title={t('退出登录', 'Sign out')}
                  onClick={() => void auth.logout()}
                >
                  <LogOut aria-hidden="true" />
                </button>
              </div>
            ) : null}
          </div>
        </header>
        <main id="main-content">
          <Outlet />
        </main>
      </div>
      {menuOpen ? (
        <button
          type="button"
          className="sidebar-scrim"
          aria-label={t('关闭导航遮罩', 'Close navigation overlay')}
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
    </div>
  );
}
